import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DeviceEventEmitter } from 'react-native';
import {
  EMPTY_PRODUCT_FORM,
  PARENT_VARIANT_THUMBNAIL_CAMERA_ID,
  createEmptyVariantRow,
  getVariantRowId,
  parseVariantAttributeNames,
  syncVariantAttributes,
  type EditableVariant,
  type ProductFormData,
  type ProductPriceType,
} from './productFormTypes';
import type { ProductVariant } from '../../../redux/productSlice';
import {
  getActiveVariantRows,
  getVariantValidationError,
} from './productFormValidation';

/**
 * Per-instance product form state (contract §6.1, per-instance form
 * ownership). Unlike the global `ProductContext`, every mounted provider owns
 * an isolated copy of the form so wizard and normal product flows can never
 * leak draft data into each other. Semantics mirror NewProduct.tsx exactly.
 */

export interface ProductFormStateValue {
  formData: ProductFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  setField: (field: keyof ProductFormData, value: string) => void;
  priceType: ProductPriceType | string;
  setPriceType: (priceType: ProductPriceType | string) => void;
  hasVariants: boolean;
  setHasVariants: (hasVariants: boolean) => void;
  variantAttributeNames: string[];
  variantAttributeInput: string;
  updateVariantAttributeNamesFromInput: (value: string) => void;
  variantRows: EditableVariant[];
  activeVariantRows: EditableVariant[];
  variantValidationError: string | null;
  addVariantRow: () => void;
  setVariantField: (
    targetVariantId: string,
    field: keyof ProductVariant,
    value: string | number | Record<string, string>,
  ) => void;
  setVariantAttributeValue: (
    targetVariantId: string,
    attributeName: string,
    value: string,
  ) => void;
  toggleSoftDeleteVariant: (targetVariantId: string) => void;
  activeVariantCameraId: string | null;
  setActiveVariantCameraId: (id: string | null) => void;
  /** Set before opening the scanner to route BARCODE_SCANNED to a variant. */
  scanTargetVariantId: React.MutableRefObject<string | null>;
  isImageProcessing: boolean;
  setIsImageProcessing: (processing: boolean) => void;
  /** True once the user has entered anything (unsaved-leave guard input). */
  isDirty: boolean;
  /** Set after a successful submit to release the unsaved-leave guard. */
  didSubmitRef: React.MutableRefObject<boolean>;
  /** Resets every piece of per-instance state back to a pristine form. */
  resetForm: () => void;
}

const ProductFormStateContext = createContext<ProductFormStateValue | null>(
  null,
);

export const useProductFormState = (): ProductFormStateValue => {
  const value = useContext(ProductFormStateContext);
  if (!value) {
    throw new Error(
      'useProductFormState must be used within a ProductFormProvider',
    );
  }
  return value;
};

export const ProductFormProvider: React.FC<{
  children: React.ReactNode;
  /** Seed simplified draft values (wizard resume). Applied on mount only. */
  initialFormData?: Partial<ProductFormData>;
  /** Seed the price type (a per-hour service draft). Applied on mount only. */
  initialPriceType?: ProductPriceType | string;
}> = ({ children, initialFormData, initialPriceType = 'item' }) => {
  const [formData, setFormData] = useState<ProductFormData>(() => ({
    ...EMPTY_PRODUCT_FORM,
    ...(initialFormData || {}),
  }));
  const formDataRef = useRef(formData);
  const [priceType, setPriceType] = useState<ProductPriceType | string>(
    initialPriceType,
  );
  const [hasVariants, setHasVariants] = useState(false);
  const [variantAttributeNames, setVariantAttributeNames] = useState<string[]>(
    [],
  );
  const [variantAttributeInput, setVariantAttributeInput] = useState('');
  const [variantRows, setVariantRows] = useState<EditableVariant[]>([]);
  const [activeVariantCameraId, setActiveVariantCameraId] = useState<
    string | null
  >(null);
  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const scanTargetVariantId = useRef<string | null>(null);
  const didSubmitRef = useRef(false);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const setField = useCallback(
    (field: keyof ProductFormData, value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const addVariantRow = useCallback(() => {
    const nextVariant = createEmptyVariantRow(variantAttributeNames);
    setVariantRows(prev => [...prev, nextVariant]);
  }, [variantAttributeNames]);

  const setVariantField = useCallback(
    (
      targetVariantId: string,
      field: keyof ProductVariant,
      value: string | number | Record<string, string>,
    ) => {
      setVariantRows(prev =>
        prev.map(variant =>
          (variant._id || variant._localId) === targetVariantId
            ? ({ ...variant, [field]: value } as EditableVariant)
            : variant,
        ),
      );
    },
    [],
  );

  const setVariantAttributeValue = useCallback(
    (targetVariantId: string, attributeName: string, value: string) => {
      setVariantRows(prev =>
        prev.map(variant => {
          if ((variant._id || variant._localId) !== targetVariantId) {
            return variant;
          }
          return {
            ...variant,
            bp_variant_attributes: {
              ...(variant.bp_variant_attributes || {}),
              [attributeName]: value,
            },
          };
        }),
      );
    },
    [],
  );

  const toggleSoftDeleteVariant = useCallback((targetVariantId: string) => {
    setVariantRows(prev =>
      prev.map(variant =>
        (variant._id || variant._localId) === targetVariantId
          ? { ...variant, _softDeleted: !variant._softDeleted }
          : variant,
      ),
    );
  }, []);

  const updateVariantAttributeNamesFromInput = useCallback((value: string) => {
    setVariantAttributeInput(value);
    const parsed = parseVariantAttributeNames(value);
    setVariantAttributeNames(parsed);
    setVariantRows(prev => syncVariantAttributes(prev, parsed));
  }, []);

  // Barcode scans route to the targeted variant row, else the root barcode
  // field — same listener contract as NewProduct.tsx.
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(
      'BARCODE_SCANNED',
      (code: string) => {
        const targetId = scanTargetVariantId.current;
        if (targetId) {
          scanTargetVariantId.current = null;
          setVariantRows(prev =>
            prev.map(variant =>
              (variant._id || variant._localId) === targetId
                ? { ...variant, bp_variant_barcode: code }
                : variant,
            ),
          );
        } else {
          setFormData({
            ...formDataRef.current,
            barcode: code,
          });
        }
      },
    );
    return () => sub.remove();
  }, []);

  // Keep at least one variant row while in variant mode.
  useEffect(() => {
    if (!hasVariants) return;
    if (!variantRows.length) {
      addVariantRow();
    }
  }, [addVariantRow, hasVariants, variantRows.length]);

  // Clear stale camera targets when the active variant row disappears.
  useEffect(() => {
    if (!hasVariants) {
      setActiveVariantCameraId(null);
      return;
    }
    if (
      !activeVariantCameraId ||
      activeVariantCameraId === PARENT_VARIANT_THUMBNAIL_CAMERA_ID
    ) {
      return;
    }
    const hasCurrentActive = variantRows.some(
      variant =>
        !variant._softDeleted &&
        getVariantRowId(variant) === activeVariantCameraId,
    );
    if (!hasCurrentActive) {
      setActiveVariantCameraId(null);
    }
  }, [activeVariantCameraId, hasVariants, variantRows]);

  const activeVariantRows = useMemo(
    () => getActiveVariantRows(variantRows),
    [variantRows],
  );

  const variantValidationError = useMemo(
    () =>
      getVariantValidationError({
        hasVariants,
        variantAttributeNames,
        activeVariantRows,
      }),
    [activeVariantRows, hasVariants, variantAttributeNames],
  );

  const isDirty = useMemo(
    () =>
      Boolean(formData.productname.trim()) ||
      Boolean(formData.category.trim()) ||
      Boolean(formData.buyingprice.trim()) ||
      Boolean(formData.sellingprice.trim()) ||
      Boolean(formData.quantity.trim()) ||
      Boolean(formData.bp_product_image) ||
      Boolean(formData.bp_product_image_uri) ||
      hasVariants,
    [formData, hasVariants],
  );

  const resetForm = useCallback(() => {
    setFormData({ ...EMPTY_PRODUCT_FORM });
    setPriceType('item');
    setHasVariants(false);
    setVariantAttributeNames([]);
    setVariantAttributeInput('');
    setVariantRows([]);
    setActiveVariantCameraId(null);
    setIsImageProcessing(false);
    scanTargetVariantId.current = null;
  }, []);

  const value = useMemo<ProductFormStateValue>(
    () => ({
      formData,
      setFormData,
      setField,
      priceType,
      setPriceType,
      hasVariants,
      setHasVariants,
      variantAttributeNames,
      variantAttributeInput,
      updateVariantAttributeNamesFromInput,
      variantRows,
      activeVariantRows,
      variantValidationError,
      addVariantRow,
      setVariantField,
      setVariantAttributeValue,
      toggleSoftDeleteVariant,
      activeVariantCameraId,
      setActiveVariantCameraId,
      scanTargetVariantId,
      isImageProcessing,
      setIsImageProcessing,
      isDirty,
      didSubmitRef,
      resetForm,
    }),
    [
      formData,
      setField,
      priceType,
      hasVariants,
      variantAttributeNames,
      variantAttributeInput,
      updateVariantAttributeNamesFromInput,
      variantRows,
      activeVariantRows,
      variantValidationError,
      addVariantRow,
      setVariantField,
      setVariantAttributeValue,
      toggleSoftDeleteVariant,
      activeVariantCameraId,
      isImageProcessing,
      isDirty,
      resetForm,
    ],
  );

  return (
    <ProductFormStateContext.Provider value={value}>
      {children}
    </ProductFormStateContext.Provider>
  );
};

export default ProductFormProvider;
